import { useEffect, useState } from 'react';
import WORK, { WORK_MS } from '../content/work';
import useReducedMotion from '../hooks/useReducedMotion';
import useViewportWidth from '../hooks/useViewportWidth';

const pad2 = n => String(n).padStart(2, '0');
const TOTAL = WORK.length;
const GAP = 16;

export default function WorkCarousel() {
  const reduced = useReducedMotion();
  const w = useViewportWidth();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const autoplay = !reduced && !paused;

  useEffect(() => {
    if (!autoplay) { setPlaying(false); return undefined; }
    setPlaying(false);
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setPlaying(true)); });
    const timer = setTimeout(() => setIndex(i => (i + 1) % TOTAL), WORK_MS);
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(timer); };
  }, [index, autoplay]);

  // Track starts at the container's left edge and bleeds off the right.
  const isMobile = w < 900;
  const pad = Math.max((w - 1280) / 2, 0) + Math.min(Math.max(w * 0.04, 20), 40);
  const slideW = Math.round(isMobile ? w * 0.82 : Math.min(Math.max(w * 0.36, 300), 560));
  const visible = Math.max(1, Math.floor((w - pad) / (slideW + GAP)));
  const shift = Math.min(index, Math.max(0, TOTAL - visible));
  const go = i => setIndex(((i % TOTAL) + TOTAL) % TOTAL);
  const progress = autoplay ? (playing ? '100%' : '0%') : `${((index + 1) / TOTAL) * 100}%`;

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);
  const navBtn = 'h-[52px] w-[52px] border-2 border-bg bg-transparent text-[20px] text-bg transition-colors hover:border-accent hover:bg-accent';

  return (
    <section
      id="work" aria-roledescription="carousel" aria-labelledby="carousel-h"
      onMouseEnter={pause} onMouseLeave={resume} onFocus={pause} onBlur={resume}
      className="overflow-hidden bg-ink pb-[clamp(48px,6vw,72px)] pt-[clamp(64px,8vw,104px)] text-bg"
    >
      <div className="container-site mb-7 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
        <h2 id="carousel-h" className="text-h2">Selected work</h2>
        <div className="flex items-center">
          <span className="pr-4 text-[15px] font-extrabold">{index + 1}/{TOTAL}</span>
          <div aria-hidden="true" className="mr-4 h-1 w-[clamp(64px,10vw,140px)] bg-neutral-800">
            <div className="h-full bg-accent" style={{ width: progress, transition: autoplay && playing ? `width ${WORK_MS}ms linear` : 'width .3s ease' }} />
          </div>
          <button type="button" onClick={() => go(index - 1)} aria-label="Previous project" className={navBtn}>←</button>
          <button type="button" onClick={() => go(index + 1)} aria-label="Next project" className={`-ml-[2px] ${navBtn}`}>→</button>
        </div>
      </div>

      <div style={{ paddingLeft: pad }}>
        <div className="flex w-max gap-4 transition-transform duration-700 ease-track" style={{ transform: `translateX(${-shift * (slideW + GAP)}px)` }}>
          {WORK.map((s, k) => {
            const on = k === index;
            return (
              <article key={s.title} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${TOTAL}: ${s.title}`} className="flex-none transition-opacity duration-500" style={{ width: slideW, opacity: on ? 1 : 0.55 }}>
                <div className="bw relative aspect-[4/3] overflow-hidden bg-neutral-900">
                  <img src={s.image} alt={s.alt} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div aria-hidden="true" className={`h-1.5 transition-colors duration-[400ms] ${on ? 'bg-accent' : 'bg-neutral-800'}`} />
                <button type="button" onClick={() => go(k)} className="block w-full pt-4 text-left text-bg">
                  <span className="flex justify-between text-label uppercase text-accent-400"><span>{s.sector}</span><span>{pad2(k + 1)}</span></span>
                  <span className="mt-2 block text-[clamp(20px,1.8vw,26px)] font-extrabold leading-[1.1] tracking-[-0.02em]">{s.title}</span>
                  <span className="mt-1.5 block text-[15px] text-neutral-400">{s.result}</span>
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
